import { useState, useRef, useCallback } from 'react'
import { Plus, Minus } from '@digital-twins/design-system/icons'
import { cn } from '@/lib/utils'
import api from '@/lib/api/client'
import type { AudienceTag } from '@digital-twins/shared-types'

interface AudienceTagsDisplayProps {
  tags: Array<AudienceTag>
  audienceId: string
}

export function AudienceTagsDisplay({ tags, audienceId }: AudienceTagsDisplayProps) {
  const validTags = tags.filter(
    (t): t is AudienceTag =>
      Boolean(t) &&
      typeof t === 'object' &&
      !Array.isArray(t) &&
      typeof t.id === 'string' &&
      Array.isArray(t.conditions),
  )

  if (validTags.length === 0) return null

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-card-foreground font-bold text-base leading-[1.5]">
        Audience Subsegments
      </h3>
      <div className="flex flex-col gap-1.5">
        {validTags.map((tag) => (
          <SegmentRow key={tag.id} tag={tag} audienceId={audienceId} />
        ))}
      </div>
    </div>
  )
}

function SegmentRow({ tag, audienceId }: { tag: AudienceTag; audienceId: string }) {
  const alreadyGenerated = Boolean(tag.description)

  const [open, setOpen] = useState(false)
  const [description, setDescription] = useState<string | null>(tag.description ?? null)
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(alreadyGenerated)
  const contentRef = useRef<HTMLDivElement>(null)

  const fetchDescription = useCallback(async () => {
    if (hasLoaded) return
    setIsLoading(true)
    try {
      const { data } = await api.post<{ description: string }>(
        `/populations/${audienceId}/segment-description`,
        {
          tagId: tag.id,
          tagName: tag.name,
          conditions: tag.conditions,
        },
      )
      setDescription(data.description)
    } catch {
      setDescription(null)
    } finally {
      setIsLoading(false)
      setHasLoaded(true)
    }
  }, [audienceId, tag, hasLoaded])

  const handleToggle = useCallback(() => {
    setOpen((v) => {
      const next = !v
      if (next) fetchDescription()
      return next
    })
  }, [fetchDescription])

  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/40 transition-colors"
      >
        <span className="text-base font-medium text-card-foreground">
          {tag.name}
        </span>
        <span className="flex-shrink-0 ml-3 flex items-center justify-center size-5 rounded-full bg-primary/15 text-primary transition-transform duration-300">
          {open ? (
            <Minus className="size-3" strokeWidth={2.5} />
          ) : (
            <Plus className="size-3" strokeWidth={2.5} />
          )}
        </span>
      </button>

      <div
        className={cn(
          'grid transition-all duration-300 ease-in-out',
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div ref={contentRef} className="overflow-hidden">
          <div className="px-4 pb-5 pt-4 border-t border-border bg-muted/20">
            {isLoading ? (
              <DescriptionSkeleton />
            ) : description ? (
              <p className="text-sm text-card-foreground leading-relaxed">{description}</p>
            ) : (
              <ConditionFallback tag={tag} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function DescriptionSkeleton() {
  return (
    <div className="flex flex-col gap-2 animate-pulse">
      <div className="h-3.5 rounded-full bg-muted w-full" />
      <div className="h-3.5 rounded-full bg-muted w-5/6" />
      <div className="h-3.5 rounded-full bg-muted w-4/6" />
    </div>
  )
}

function ConditionFallback({ tag }: { tag: AudienceTag }) {
  return (
    <div className="flex flex-col gap-4">
      {tag.conditions.map((condition, idx) => (
        <div key={idx} className="flex flex-col gap-1">
          {tag.conditions.length > 1 && (
            <span className="text-xs font-medium text-muted-foreground">
              {idx === 0 ? 'Condition 1' : `Condition ${idx + 1}`}
            </span>
          )}
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-card-foreground">
              {condition.sourceColumn}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {condition.values.map((val) => (
                <span
                  key={val}
                  className={cn(
                    'inline-flex items-center rounded-full px-3 py-0.5',
                    'bg-primary/15 text-sm font-medium text-foreground',
                  )}
                >
                  {val}
                </span>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
